import {
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    TranslationOutlined,
    UploadOutlined,
} from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-layout';
import {
    Button,
    Dropdown,
    Form,
    Input,
    InputNumber,
    Modal,
    notification,
    Space,
    Spin,
    Table,
    Tag,
    Tooltip,
    Upload,
} from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnType } from 'antd/lib/table/interface';
import type { RcFile } from 'antd/lib/upload';
import { useTravelThemes } from 'api/travel-themes.api';
import { useFormik } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';
import type { TravelThemeSchema } from 'typings/travel-theme/TravelThemeSchema';
import { uploadRawFile } from 'utils/image/image-upload';
import * as yup from 'yup';
import styles from './TravelThemes.module.css';

const SUPPORTED_LANGUAGES = [
    { code: 'en', label: 'English' },
    { code: 'nl', label: 'Dutch' },
    { code: 'fr', label: 'French' },
    { code: 'de', label: 'German' },
    { code: 'pt', label: 'Portuguese' },
    { code: 'es', label: 'Spanish' },
] as const;

type TranslationFormValues = Record<string, string>;

type ThemeFormValues = {
    name: string;
    icon_path: string | null;
    sort_order: number;
    translations: TranslationFormValues;
};

type LocaleCode = typeof SUPPORTED_LANGUAGES[number]['code'];

const emptyTranslations: TranslationFormValues = Object.fromEntries(
    SUPPORTED_LANGUAGES.filter((l) => l.code !== 'en').map((l) => [l.code, '']),
);

const emptyForm: ThemeFormValues = {
    name: '',
    icon_path: null,
    sort_order: 1,
    translations: { ...emptyTranslations },
};

const themeFormSchema = yup.object({
    name: yup.string().trim().required('Name is required'),
    icon_path: yup.string().nullable().required('Icon is required'),
    sort_order: yup.number().min(1, 'Position must be 1 or greater').required(),
});

export function TravelThemesPage() {
    const { themes, isLoading, saveTheme, removeTheme } = useTravelThemes();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [editingThemeId, setEditingThemeId] = useState<number | null>(null);
    const [iconPreview, setIconPreview] = useState<string | null>(null);
    const [locale, setLocale] = useState<LocaleCode>('en');
    const isEditing = editingThemeId !== null;
    const formik = useFormik<ThemeFormValues>({
        initialValues: emptyForm,
        validationSchema: themeFormSchema,
        onSubmit: async (values, helpers) => {
            try {
                const body = {
                    name: values.name.trim(),
                    icon_path: values.icon_path,
                    sort_order: values.sort_order,
                };

                await saveTheme({
                    themeId: editingThemeId ?? undefined,
                    body,
                    translations: values.translations,
                });

                notification.success({
                    message: isEditing
                        ? 'Theme updated successfully'
                        : 'Theme created successfully',
                });
                closeModal();
            } catch {
                notification.error({
                    message: `Failed to ${
                        isEditing ? 'update' : 'create'
                    } theme`,
                });
            } finally {
                helpers.setSubmitting(false);
            }
        },
    });

    const handleOpenModal = useCallback(
        (values: ThemeFormValues, themeId: number | null) => {
            setEditingThemeId(themeId);
            setLocale('en');
            formik.resetForm({ values });
            setIconPreview(null);
            setIsModalOpen(true);
        },
        [formik],
    );

    const handleOpenCreate = useCallback(() => {
        handleOpenModal(getCreateFormValues(themes), null);
    }, [handleOpenModal, themes]);

    const handleOpenEdit = useCallback(
        (theme: TravelThemeSchema) => {
            handleOpenModal(getEditFormValues(theme), theme.id);
        },
        [handleOpenModal],
    );

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
        setEditingThemeId(null);
        formik.resetForm({ values: emptyForm });
        setIconPreview(null);
        setLocale('en');
    }, [formik]);

    const handleCloseAttempt = useCallback(() => {
        if (formik.dirty || isUploading) {
            Modal.confirm({
                title: 'Unsaved changes',
                content:
                    'You have unsaved changes. Are you sure you want to discard them?',
                okText: 'Discard',
                okButtonProps: { danger: true },
                cancelText: 'Keep editing',
                onOk: closeModal,
            });
            return;
        }
        closeModal();
    }, [formik.dirty, isUploading, closeModal]);

    const localizedNameField =
        locale === 'en' ? 'name' : `translations.${locale}`;
    const localizedNameValue =
        locale === 'en'
            ? formik.values.name
            : formik.values.translations[locale] ?? '';

    const languageMenuProps = useMemo<MenuProps>(
        () => ({
            items: SUPPORTED_LANGUAGES.map((language) => ({
                key: language.code,
                label: language.label,
            })),
            selectedKeys: [locale],
            onClick: ({ key }) => setLocale(key as LocaleCode),
        }),
        [locale],
    );

    const handleDelete = useCallback(
        (theme: TravelThemeSchema) => {
            Modal.confirm({
                title: 'Delete travel theme?',
                content: `Are you sure you want to delete "${theme.name}"? This will also remove it from all destinations.`,
                okText: 'Delete',
                okButtonProps: { danger: true },
                onOk: async () => {
                    try {
                        await removeTheme(theme.id);
                        notification.success({
                            message: 'Theme deleted successfully',
                        });
                    } catch {
                        notification.error({
                            message: 'Failed to delete theme',
                        });
                    }
                },
            });
        },
        [removeTheme],
    );

    const { setFieldValue } = formik;
    const handleIconUpload = useCallback(
        async (file: RcFile) => {
            if (file.type !== 'image/png') {
                notification.error({ message: 'Only PNG files are allowed' });
                return Upload.LIST_IGNORE;
            }

            setIconPreview(URL.createObjectURL(file));
            setIsUploading(true);
            try {
                const url = await uploadRawFile(file);
                setFieldValue('icon_path', url);
            } catch {
                setIconPreview(null);
                notification.error({ message: 'Failed to upload icon' });
            } finally {
                setIsUploading(false);
            }
            return false;
        },
        [setFieldValue],
    );

    const columns = useMemo<ColumnType<TravelThemeSchema>[]>(() => {
        const cellStyle = { style: { verticalAlign: 'middle' as const } };
        return [
            {
                title: 'Icon',
                dataIndex: 'icon_path',
                key: 'icon_path',
                width: 80,
                onCell: () => cellStyle,
                render: (_: unknown, theme: TravelThemeSchema) =>
                    theme.icon_path ? (
                        <img
                            src={theme.icon_path}
                            alt=""
                            width={32}
                            height={32}
                            className={styles.table__icon}
                            onError={(e) => {
                                e.currentTarget.style.display = 'none';
                            }}
                        />
                    ) : (
                        <span className={styles.table__placeholder}>--</span>
                    ),
            },
            {
                title: 'Name',
                dataIndex: 'name',
                key: 'name',
                onCell: () => cellStyle,
                sorter: (a, b) => a.name.localeCompare(b.name),
            },
            {
                title: 'Translations',
                key: 'translations',
                width: 200,
                onCell: () => cellStyle,
                render: (_: unknown, theme: TravelThemeSchema) => {
                    const translated =
                        theme.translations?.filter(
                            (t) => t.language !== 'en',
                        ) ?? [];
                    if (translated.length === 0) {
                        return (
                            <span className={styles.table__placeholder}>
                                None
                            </span>
                        );
                    }
                    return (
                        <Space size={4} wrap>
                            {translated.map((t) => (
                                <Tooltip key={t.language} title={t.name}>
                                    <Tag>{t.language.toUpperCase()}</Tag>
                                </Tooltip>
                            ))}
                        </Space>
                    );
                },
            },
            {
                title: 'Position',
                dataIndex: 'sort_order',
                key: 'sort_order',
                width: 120,
                onCell: () => cellStyle,
                sorter: (a, b) => a.sort_order - b.sort_order,
                defaultSortOrder: 'ascend',
            },
            {
                title: 'Actions',
                key: 'actions',
                width: 120,
                onCell: () => cellStyle,
                render: (_: unknown, theme: TravelThemeSchema) => (
                    <Space>
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleOpenEdit(theme)}
                        />
                        <Button
                            size="small"
                            icon={<DeleteOutlined />}
                            danger
                            onClick={() => handleDelete(theme)}
                        />
                    </Space>
                ),
            },
        ];
    }, [handleOpenEdit, handleDelete]);

    return (
        <div className="full-page">
            <PageHeader
                ghost={false}
                title="Travel Themes"
                extra={
                    <Button
                        type="primary"
                        size="large"
                        icon={<PlusOutlined />}
                        onClick={handleOpenCreate}
                    >
                        Add theme
                    </Button>
                }
            />
            <div className="full-page-content">
                <div className="max-content-width mb-m">
                    <Table
                        dataSource={themes.map((t) => ({ ...t, key: t.id }))}
                        columns={columns}
                        loading={isLoading}
                        pagination={false}
                    />
                </div>
            </div>

            <Modal
                title={isEditing ? 'Edit Travel Theme' : 'Add Travel Theme'}
                open={isModalOpen}
                onCancel={handleCloseAttempt}
                onOk={formik.submitForm}
                confirmLoading={formik.isSubmitting}
                okButtonProps={{ disabled: isUploading || formik.isSubmitting }}
                okText={isEditing ? 'Save' : 'Create'}
                destroyOnClose
            >
                <Form
                    layout="vertical"
                    className={styles.form__fields}
                    onFinish={formik.submitForm}
                >
                    <div>
                        <label
                            htmlFor="theme-name"
                            className={styles.form__label}
                        >
                            Name
                        </label>
                        <Form.Item
                            validateStatus={
                                formik.touched.name && formik.errors.name
                                    ? 'error'
                                    : undefined
                            }
                            help={
                                formik.touched.name
                                    ? formik.errors.name
                                    : undefined
                            }
                        >
                            <div className={styles.form__nameRow}>
                                <Input
                                    id="theme-name"
                                    value={localizedNameValue}
                                    onChange={(e) =>
                                        formik.setFieldValue(
                                            localizedNameField,
                                            e.target.value,
                                        )
                                    }
                                    placeholder={
                                        locale === 'en'
                                            ? 'Theme name'
                                            : `Name in ${locale.toUpperCase()}`
                                    }
                                    size="large"
                                    className={styles.form__nameInput}
                                    status={
                                        formik.touched.name &&
                                        formik.errors.name
                                            ? 'error'
                                            : undefined
                                    }
                                />
                                <Dropdown
                                    menu={languageMenuProps}
                                    trigger={['click']}
                                >
                                    <Button
                                        size="large"
                                        icon={<TranslationOutlined />}
                                    >
                                        {locale.toUpperCase()}
                                    </Button>
                                </Dropdown>
                            </div>
                        </Form.Item>
                    </div>
                    <div>
                        <span className={styles.form__label}>Icon</span>
                        <div className={styles.form__iconRow}>
                            {(iconPreview || formik.values.icon_path) && (
                                <img
                                    src={
                                        iconPreview ??
                                        formik.values.icon_path ??
                                        ''
                                    }
                                    alt=""
                                    width={40}
                                    height={40}
                                    className={styles.form__iconPreview}
                                />
                            )}
                            <Upload
                                accept=".png"
                                showUploadList={false}
                                beforeUpload={handleIconUpload}
                            >
                                <Button
                                    icon={
                                        isUploading ? (
                                            <Spin size="small" />
                                        ) : (
                                            <UploadOutlined />
                                        )
                                    }
                                    disabled={isUploading}
                                >
                                    {formik.values.icon_path
                                        ? 'Replace icon'
                                        : 'Upload icon'}
                                </Button>
                            </Upload>
                            {(iconPreview || formik.values.icon_path) && (
                                <Button
                                    icon={<DeleteOutlined />}
                                    danger
                                    onClick={() => {
                                        setIconPreview(null);
                                        formik.setFieldValue('icon_path', null);
                                    }}
                                />
                            )}
                        </div>
                        <span
                            className={
                                formik.touched.icon_path &&
                                formik.errors.icon_path
                                    ? styles.form__hintError
                                    : styles.form__hint
                            }
                        >
                            {(formik.touched.icon_path &&
                                formik.errors.icon_path) ??
                                'Only PNG format is supported'}
                        </span>
                    </div>
                    <div>
                        <label
                            htmlFor="theme-position"
                            className={styles.form__label}
                        >
                            Position
                        </label>
                        <InputNumber
                            id="theme-position"
                            value={formik.values.sort_order}
                            onChange={(value) =>
                                formik.setFieldValue('sort_order', value ?? 1)
                            }
                            min={1}
                            size="large"
                            className={styles.form__positionInput}
                        />
                    </div>
                </Form>
            </Modal>
        </div>
    );
}

function getCreateFormValues(themes: TravelThemeSchema[]): ThemeFormValues {
    return {
        ...emptyForm,
        translations: { ...emptyTranslations },
        sort_order:
            themes.length > 0
                ? Math.max(...themes.map((theme) => theme.sort_order)) + 1
                : 1,
    };
}

function getEditFormValues(theme: TravelThemeSchema): ThemeFormValues {
    const translations = { ...emptyTranslations };
    theme.translations?.forEach((translation) => {
        if (translation.language !== 'en') {
            translations[translation.language] = translation.name;
        }
    });

    return {
        name: theme.name,
        icon_path: theme.icon_path ?? null,
        sort_order: theme.sort_order,
        translations,
    };
}
